import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * Открытая карточка объявления и возврат на своё место (T11.8, T11.10).
 *
 * Карточка открывается из трёх списков — выдачи поиска, избранного и
 * истории уведомлений. Правило возврата у них одно, а три его копии
 * разошлись бы: в одном списке прокрутку сохранили бы, в другом забыли,
 * и заметил бы это только пользователь.
 *
 * ## Список не размонтируется, а прячется
 *
 * Это условие, а не подробность. Убери список из дерева — и вместе с ним
 * пропадут загруженные страницы: возврат начинал бы всё заново, с первой
 * страницы. Хук хранит только «что открыто» и «куда вернуть»; прятать
 * список — дело экрана, для чего и отдаётся класс обёртки.
 *
 * ## Возврат до отрисовки
 *
 * `useLayoutEffect`, а не `useEffect`: второй вернул бы прокрутку кадром
 * позже, и человек увидел бы прыжок от начала списка к своему месту.
 */
export interface Opened {
  /** Что открыто; `null` — открыт сам список. */
  readonly opened: string | number | null;
  readonly open: (listingId: string | number) => void;
  readonly back: () => void;
  /** Класс обёртки списка: она прячется, пока открыта карточка. */
  readonly flow: string;
}

export function useOpened(): Opened {
  const [opened, setOpened] = useState<string | number | null>(null);
  // Человек нашёл объявление на середине третьей страницы; вернуть его
  // в начало списка значит заставить листать заново — и заново гадать,
  // какие из них он уже смотрел
  const wasAt = useRef(0);

  const open = useCallback((listingId: string | number) => {
    wasAt.current = window.scrollY;
    setOpened(listingId);
  }, []);

  const back = useCallback(() => {
    setOpened(null);
  }, []);

  useLayoutEffect(() => {
    window.scrollTo(0, opened === null ? wasAt.current : 0);
  }, [opened]);

  return { opened, open, back, flow: opened === null ? 'flow' : 'flow flow--hidden' };
}
